// CC-351 — internal Terms applications list + detail; Terms pass A adds the review screen and settings.
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { PUBLIC_TERMS_BASE, useTermsScope } from "@/lib/termsAccess";
import { TermsReview } from "@/components/terms/TermsReview";
import { TermsSettingsPanel } from "@/components/terms/TermsSettingsPanel";
import { StepIndicator } from "@/components/terms/StepIndicator";
import { useAuth } from "@/lib/auth";

const STATUSES = ["received", "missing_info", "verifying", "recommended", "approved", "denied", "not_eligible", "withdrawn"];
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { timeZone: "America/Chicago", dateStyle: "medium", timeStyle: "short" }) : "");
const errText = (e) => e?.response?.data?.detail || e.message;

function Row({ label, value, testId }) {
  return <div className="grid grid-cols-3 gap-3 py-1.5 text-sm border-b border-[#f1f2f4]"><div className="text-[#6b7280]">{label}</div><div className="col-span-2 text-[#0e1420]" data-testid={testId}>{value ?? "—"}</div></div>;
}

function VoidPanel({ id, params, onVoided }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async () => {
    if (reason.trim().length < 5) { setErr("Reason must be at least 5 characters."); return; }
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post(`/terms/applications/${id}/void`, { reason: reason.trim() }, { params });
      onVoided(data);
      setOpen(false); setReason("");
    } catch (e) { setErr(String(e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  if (!open) return (
    <button className="px-3 py-1.5 rounded text-xs font-semibold bg-[#f5f6f8] border border-[#e0e3ea] text-[#6b7280] hover:bg-[#fdecec] hover:text-[#c73535] hover:border-[#f4c9c9] transition-colors" onClick={() => setOpen(true)} data-testid="terms-void-open-btn">Void test record</button>
  );
  return (
    <div className="bg-[#fdecec] border border-[#f4c9c9] rounded-lg p-4 space-y-2" data-testid="terms-void-panel">
      <div className="text-sm font-semibold text-[#c73535]">Void this test record</div>
      <div className="text-xs text-[#6b7280]">This sets the application to Voided. No email is sent and no register row is created. The record stays visible under Completed with a Voided label.</div>
      <textarea className="w-full rounded border border-[#f4c9c9] px-3 py-2 text-sm" rows={2} placeholder="Type the reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="terms-void-reason" />
      {err && <div className="text-xs text-[#c73535]">{err}</div>}
      <div className="flex gap-2">
        <button className="px-3 py-1.5 rounded text-xs font-semibold bg-[#c73535] text-white" onClick={submit} disabled={busy} data-testid="terms-void-confirm-btn">{busy ? "Voiding…" : "Confirm void"}</button>
        <button className="px-3 py-1.5 rounded text-xs font-semibold border border-[#e0e3ea]" onClick={() => { setOpen(false); setReason(""); setErr(null); }} data-testid="terms-void-cancel-btn">Cancel</button>
      </div>
    </div>
  );
}

function Detail({ id, slug, scope }) {
  const { user } = useAuth();
  const [rec, setRec] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [err, setErr] = useState(null);
  const params = { tenant_slug: scope.tenant_slug };
  useEffect(() => {
    api.get(`/terms/applications/${id}`, { params }).then(({ data }) => setRec(data)).catch((e) => setErr(String(errText(e))));
    api.get("/terms/settings", { params }).then(({ data }) => setReasons(data.terms_denial_reasons || [])).catch(() => {});
  }, [id, scope.tenant_slug]); // eslint-disable-line react-hooks/exhaustive-deps
  if (err) return <div className="text-sm text-[#b91c1c]" data-testid="terms-detail-error">{err}</div>;
  if (!rec) return null;
  const openPdf = async () => {
    const { data } = await api.get(`/terms/applications/${id}/pdf`, { responseType: "blob", params });
    window.open(URL.createObjectURL(data), "_blank");
  };
  return (
    <div className="space-y-6" data-testid="terms-detail">
      <Link to={`/${slug}/app/commercial-accounts/new`} className="text-sm text-[#0072bc]" data-testid="terms-back-link">← Commercial Accounts</Link>
      <div className="bg-white border border-[#e5e7eb] rounded-lg p-4"><StepIndicator progress={rec.progress} testId="detail-steps" /></div>
      <h1 className="text-2xl font-semibold text-[#0e1420]" data-testid="terms-detail-company">{rec.company_name}</h1>
      {rec.redacted && <div className="text-xs text-[#6b7280]" data-testid="terms-detail-redacted">Read-only view. EIN, bank details and contact email/phone are limited to the terms process group.</div>}
      <div className="bg-white border border-[#e5e7eb] rounded-lg p-4">
        <Row label="Status" value={rec.status} testId="terms-detail-status" />
        <Row label="Source" value={rec.source} />
        <Row label="Submitted" value={when(rec.submitted_at)} />
        <Row label="Requested terms" value={`Net ${rec.requested_terms}`} />
        <Row label="Legal name" value={rec.legal_name} />
        <Row label="EIN" value={rec.ein} testId="terms-detail-ein" />
        <Row label="Address" value={rec.address} />
        <Row label="AP contact" value={[rec.ap_contact_name, rec.ap_email, rec.ap_phone].filter(Boolean).join(" · ")} />
        <Row label="Est. annual spend" value={rec.est_annual_spend != null ? `$${Number(rec.est_annual_spend).toLocaleString()}` : null} />
        <Row label="Bank" value={rec.bank ? [rec.bank.name, rec.bank.contact, rec.bank.phone].filter(Boolean).join(" · ") : null} />
        {(rec.trade_refs || []).map((r, i) => <Row key={i} label={`Trade ref ${i + 1}`} value={[r.company, r.contact, r.email, r.phone].filter(Boolean).join(" · ")} />)}
        {rec.pdf && <Row label="PDF" value={<button className="text-[#0072bc] hover:underline" onClick={openPdf} data-testid="terms-detail-pdf-btn">{rec.pdf.filename}</button>} />}
        <Row label="Slack alert" value={rec.notifications?.slack?.status} />
        <Row label="Confirmation email" value={rec.notifications?.a01?.status} />
      </div>
      <TermsReview key={rec.updated_at || rec.id} rec={rec} scope={scope} reasons={reasons} onSaved={setRec} />
      {user?.role === "platform_admin" && rec.status !== "voided" && (
        <div className="pt-2" data-testid="terms-void-section">
          <div className="text-xs font-semibold text-[#6b7280] uppercase tracking-widest mb-2">Platform admin actions</div>
          <VoidPanel id={id} params={params} onVoided={setRec} />
        </div>
      )}
      {rec.status === "voided" && (
        <div className="bg-[#f5f6f8] border border-[#e0e3ea] rounded-lg p-4 text-sm" data-testid="terms-voided-banner">
          <span className="font-semibold text-[#6b7280]">Voided</span>
          {rec.voided_by && <span className="text-[#6b7280]"> by {rec.voided_by}</span>}
          {rec.void_reason && <div className="text-xs text-[#6b7280] mt-1">Reason: {rec.void_reason}</div>}
        </div>
      )}
    </div>
  );
}

function List({ slug, scope }) {
  const [status, setStatus] = useState("");
  const [items, setItems] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/terms/applications", { params: { tenant_slug: scope.tenant_slug, status: status || undefined } })
      .then(({ data }) => setItems(data.items)).catch((e) => setErr(String(errText(e))));
  }, [status, scope.tenant_slug]);
  if (err) return <div className="text-sm text-[#b91c1c]" data-testid="terms-list-error">{err}</div>;
  return (
    <div className="space-y-4" data-testid="terms-list-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold text-[#0e1420]">Terms applications</h1>
          <p className="text-sm text-[#6b7280] mt-1">Public form: <span className="font-mono" data-testid="terms-public-link">{PUBLIC_TERMS_BASE}/{scope.tenant_slug}</span></p>
          <Link to={`/${slug}/app/terms/register`} className="text-sm text-[#0072bc]" data-testid="terms-register-link">Terms register →</Link>
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-[#cfd4dc] px-3 py-2 text-sm" data-testid="terms-status-filter">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
        </select>
      </div>
      <div className="bg-white border border-[#e5e7eb] rounded-lg divide-y divide-[#f1f2f4]">
        {items && items.length === 0 && <div className="p-6 text-sm text-[#6b7280]" data-testid="terms-list-empty">No applications.</div>}
        {(items || []).map((r) => (
          <Link key={r.id} to={`/${slug}/app/terms/${r.id}`} className="flex items-center justify-between px-4 py-3 hover:bg-[#f8f9fb]" data-testid={`terms-row-${r.id}`}>
            <div><div className="text-sm font-medium text-[#0e1420]">{r.company_name}</div><div className="text-xs text-[#6b7280]">{when(r.submitted_at)} · {r.source} · Net {r.requested_terms}</div></div>
            <span className="text-xs px-2 py-0.5 rounded-full bg-[#eef6fb] text-[#1c6a99]">{r.status}</span>
          </Link>
        ))}
      </div>
      {scope.can_edit_settings && <TermsSettingsPanel slug={scope.tenant_slug} />}
    </div>
  );
}

export default function TermsApplications() {
  const { slug, appId } = useParams();
  const { scope, err } = useTermsScope();
  if (err) return <div className="text-sm text-[#b91c1c]" data-testid="terms-scope-error">{err}</div>;
  if (!scope) return null;
  return appId ? <Detail id={appId} slug={slug} scope={scope} /> : <List slug={slug} scope={scope} />;
}
